<template>
  <footer class="footer">
    <div class="container">
      <div class="footer-columns">
        <!-- Info column -->
        <div class="column">
          <BaseText class="bold">{{ $t("footer.info") }}</BaseText>
          <NuxtLink
            v-for="page in pagesData?.websitePages ?? []"
            :key="page.path"
            :to="localePath(page.path)"
          >
            {{ page.title }}
          </NuxtLink>
        </div>

        <!-- Categories column -->
        <div class="column hide-small">
          <BaseText class="bold">{{ $t("footer.categories") }}</BaseText>
          <NuxtLink
            v-for="item in categoriesData?.categories ?? []"
            :key="item?.id"
            :to="localePath({ name: 'categories-slug', params: { slug: item!.slug } })"
          >
            {{ item!.name }}
          </NuxtLink>
        </div>

        <!-- Popular column -->
        <div class="column hide-small border-right">
          <BaseText class="bold">{{ $t("footer.popular") }}</BaseText>
          <NuxtLink
            v-for="item in popularShops[host]"
            :key="item.title"
            :to="
              localePath({
                name: 'slug-shops',
                params: {
                  slug: isProductionEnv ? item.slug : item.slugStaging,
                },
              })
            "
          >
            {{ $t("footer.brochure", { shop: item.title }) }}
          </NuxtLink>
        </div>

        <!-- Newsletter Signup & Socials column -->
        <section class="column stack spacious">
          <SignupForm v-if="host === 'myshopi'" />
          <div class="stack">
            <BaseText class="bold">{{ $t("footer.socials") }}</BaseText>
            <div class="socials-grid">
              <a v-for="item in socials[host]" :key="item.name" :href="item.url" target="_blank">
                {{ item.name }}
              </a>
            </div>
          </div>
        </section>
        <!-- Logo & buttons column -->
        <div class="column">
          <NavbarLogo class="logo dark" />
          <AppStoreButtons class="gold" />
          <ul class="unstyled">
            <li>{{ $t("footer.save-brochures") }}</li>
            <li>{{ $t("footer.brochures-near-you") }}</li>
            <li>{{ $t("footer.lots-of-downloads") }}</li>
            <li v-if="host === 'allefolders'" style="margin-block-start: 1rem">
              Check de beste aanbiedingen ook op
              <a href="https://megadeal.nl" target="_blank" rel="noopener" style="text-decoration: underline"
                >megadeal.nl</a
              >
            </li>
          </ul>
        </div>
      </div>

      <div class="footer-bottom mt-4">
        <BaseText>{{ $t(`footer.copyright-${host}`) }}</BaseText>
        <div class="footer-bottom-links">
          <a v-for="link in footerLinks" :key="link.text" :href="link.url">
            {{ $t(link.text) }}
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { popularShops } from "~/assets/data/popularShops";
import { useCategoriesQuery, useWebsitePagesQuery } from "~/graphql/generated";

const host = useCurrentHost();
const isProductionEnv = getEnvironment() === "production";
const localePath = useLocalePath();
const { data: categoriesData, executeQuery: executeCategoriesQuery } = await useCategoriesQuery({});

const { data: pagesData, executeQuery: executePagesQuery } = await useWebsitePagesQuery({});

// When the locale changes, refetch the pages and categories with the new locale (in context)
const { locale } = useI18n();
watch(locale, () => {
  executePagesQuery({
    requestPolicy: "network-only",
  });
  executeCategoriesQuery({
    requestPolicy: "network-only",
  });
});

const socials = {
  allefolders: [
    {
      name: "Facebook",
      url: "https://www.facebook.com/AlleFolders",
    },
    {
      name: "Instagram",
      url: "https://www.instagram.com/allefolders",
    },
    {
      name: "Tiktok",
      url: "https://www.tiktok.com/@allefoldersapp",
    },
    {
      name: "Pinterest",
      url: "https://www.pinterest.com/Alle_Folders/",
    },
    // {
    //   name: "LinkedIn",
    //   url: "https://www.linkedin.com/",
    // },
  ],
  promopromo: [
    {
      name: "Facebook",
      url: "https://www.facebook.com/promopromobe/",
    },
    {
      name: "Instagram",
      url: "https://www.instagram.com/promopromobe",
    },
  ],
  myshopi: [
    {
      name: "Facebook",
      url: "https://www.facebook.com/myShopi/",
    },
    {
      name: "Instagram",
      url: "https://www.instagram.com/myshopi.be",
    },
  ],
};

const footerLinks = computed(() => {
  if (host.value === "myshopi")
    return [
      {
        url: localePath("/terms-of-use"),
        text: "footer.conditions",
      },
      {
        url: localePath("/privacy-statement"),
        text: "footer.privacy",
      },
      {
        url: localePath("/cookies"),
        text: "footer.cookies",
      },
    ];
  else
    return [
      {
        url: localePath("/privacy-statement"),
        text: "footer.privacy-and-cookies",
      },
      {
        url: localePath("/disclaimer"),
        text: "footer.disclaimer",
      },
      {
        url: localePath("/contact"),
        text: "footer.contact",
      },
    ];
});
</script>

<style lang="scss" scoped>
.footer {
  background-color: $light-95;
  width: 100%;
  padding: 3rem 0 1.5rem 0;
}

.footer-columns {
  display: grid;

  .column {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1rem 0;
  }

  .spacious {
    @media (min-width: $breakpoint-medium) {
      padding-inline: 1.5rem;
    }
  }
}

.logo {
  padding: 0;
}

.footer-bottom {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  .footer-bottom-links {
    display: flex;
    gap: 1rem;
  }
}

// Styles for small screens
@include for-small-screen {
  .footer-columns {
    grid-template-columns: 1fr;
  }
  .footer-bottom {
    flex-direction: column;
  }
  .footer-bottom-links {
    flex-direction: column;
  }
}

// Tablet landscape and up
@include for-medium-screen-up {
  .footer-columns {
    grid-template-columns: 1.5fr 1.5fr 1.5fr 3fr 2.5fr;
  }
}

.border-right {
  border-right: 1px solid #d7d6d6;
}

.socials-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
</style>
